import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";

/**
 * FIX-053: the next step of a Reel the user has just approved, shown RIGHT UNDER that Reel in the Top 30: Repurpose (RP) through the same route the Approved Reels list uses, and RE ("Recreate with RE", the same link the Approved Reels list shows).
 * Get JSON is a separate, future option: a disabled button marked "Coming soon". Approving a Reel starts nothing; the video process (Create -> Video) and RE start only when the user starts them.
 */
const ERROR_TEXT: Record<string, string> = {
  rp_handoff_not_configured: "The RP hand-off is not configured on this server, so nothing was prepared.",
  rp_media_missing: "The stored source video of this Reel is not available (it was removed, or never stored). Nothing was started and nothing was fetched again.",
  rp_handoff_failed: "The source video could not be registered. Nothing was started; try again later.",
};

/** Where the video creation screen opens with this Reel's saved source video and the persona already chosen (they are only pre-selected there; nothing is submitted). */
export const videoCreationUrl = (sourceVideoId: string, personaId: string): string => `/generate/create/video?sourceVideoId=${encodeURIComponent(sourceVideoId)}&personaId=${encodeURIComponent(personaId)}`;

/** The existing RE entry (the same link the Approved Reels list uses): RE opens with this Approved Reel and the persona; the user then continues through the normal RE steps (nothing is analyzed or produced until they start it). */
export const reUrl = (srrId: string, personaId: string): string => `/generate/create/re?srrId=${encodeURIComponent(srrId)}&personaId=${encodeURIComponent(personaId)}`;

export function ReelNextStep({ root, personaId, srrId, mp4Removed, canAct, csrfToken, navigateTo }: { readonly root: string; readonly personaId: string; readonly srrId: string; readonly mp4Removed: boolean; readonly canAct: boolean; readonly csrfToken: string | null; readonly navigateTo?: ((url: string) => void) | undefined }) {
  const [handoff, setHandoff] = useState<{ sourceVideoId: string; personaDisplayName: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const errorText = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    return (body?.error !== undefined ? ERROR_TEXT[body.error] : undefined) ?? body?.message ?? fallback;
  };
  const prepareRp = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sourceVideoId: string; personaDisplayName: string }>(`${root}/srrs/${encodeURIComponent(srrId)}/rp`, csrfToken);
      if (mounted.current) setHandoff(r);
      // Straight on to the video creation screen, with the saved source video and the persona already in place. Only after the source video is really saved.
      (navigateTo ?? ((url: string) => window.location.assign(url)))(videoCreationUrl(r.sourceVideoId, personaId));
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not prepare the source video."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <div data-testid="top-reel-next-step" style={{ borderTop: "1px solid var(--border, #ccc)", marginTop: 8, paddingTop: 8 }}>
      <h5 style={{ margin: "0 0 4px" }}>Next step — Repurpose (RP)</h5>
      <p className="muted" style={{ margin: "4px 0" }}>
        This Reel is approved and saved. Nothing has started. Continue opens video creation with this Reel's saved source video and your persona already selected - you start the video there.
      </p>
      <p style={{ margin: "4px 0" }}>
        {canAct ? (
          <button type="button" data-testid="top-reel-continue-video" disabled={busy || mp4Removed} onClick={() => void prepareRp()}>
            Continue to video creation →
          </button>
        ) : null}
      </p>
      {mp4Removed ? (
        <p className="muted" data-testid="top-reel-mp4-removed">
          The saved source video of this Reel was removed, so Repurpose cannot start from it. It is not fetched again automatically.
        </p>
      ) : null}
      {handoff !== null ? (
        <p data-testid="top-reel-rp-ready">
          The source video is saved. Opening video creation with it and the persona {handoff.personaDisplayName} selected… If it does not open,{" "}
          <a data-testid="top-reel-continue-link" href={videoCreationUrl(handoff.sourceVideoId, personaId)}>
            open it here
          </a>
          . Nothing was started.
        </p>
      ) : null}
      <p style={{ margin: "4px 0" }}>
        <a data-testid="top-reel-re" href={reUrl(srrId, personaId)}>
          Recreate with RE
        </a>{" "}
        <span className="muted" data-testid="top-reel-re-note">
          Opens RE with this Reel's video and your persona; nothing starts until you start it there.
        </span>
      </p>
      <p style={{ margin: "4px 0" }}>
        <button type="button" data-testid="top-reel-get-json" disabled title="Get JSON is not available yet">
          Get JSON
        </button>{" "}
        <span className="muted" data-testid="top-reel-get-json-soon">
          Coming soon
        </span>
      </p>
      {error !== null ? (
        <p role="alert" data-testid="top-reel-next-step-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
